/**
 * Five — motion vocabulary.
 *
 * The aesthetic is "letterpress tiles on warm paper." Tiles "press" inward
 * when typed (slight scale-down with a quick spring back), and "flip + color"
 * when revealed. The flip uses a rotateX with overshoot via a loose spring so
 * the tile lands with a hint of bounce — not bouncy-castle bounce, more like
 * a soft printer's tile settling.
 */

import { easing, timings } from '@/juice';

/** Page entry — the whole composition settles in from a small offset. */
export const pageSpring = {
  type: 'spring' as const,
  stiffness: 200,
  damping: 24,
  mass: 1.1,
};

/** Hub card spring — the daily/practice tiles settle distinctly. */
export const cardSpring = {
  type: 'spring' as const,
  stiffness: 240,
  damping: 26,
  mass: 0.85,
};

/** Hover lift. */
export const hoverSpring = {
  type: 'spring' as const,
  stiffness: 360,
  damping: 22,
};

/** When a letter is typed, the tile pulses in with a snappy spring. */
export const tilePressSpring = {
  type: 'spring' as const,
  stiffness: 600,
  damping: 18,
  mass: 0.6,
};

/**
 * Tile reveal — the flip-and-color spring. Loose enough to overshoot
 * visibly without feeling jiggly.
 */
export const tileRevealSpring = {
  type: 'spring' as const,
  stiffness: 280,
  damping: 14,
  mass: 0.9,
};

/** Win-state tile victory dance — a bigger overshoot than reveal. */
export const winSpring = {
  type: 'spring' as const,
  stiffness: 320,
  damping: 10,
  mass: 0.8,
};

/** Shake spring for "not a word." Fast, decaying. */
export const shakeSpring = {
  type: 'spring' as const,
  stiffness: 700,
  damping: 12,
};

/** Stagger between tile reveals within a single row, in ms. */
export const REVEAL_STAGGER_MS = 110;
/** How long one tile's reveal takes. */
export const REVEAL_DURATION_MS = 520;
/** How long the full row reveal cascade lasts (5 tiles + last reveal). */
export const ROW_REVEAL_MS = REVEAL_STAGGER_MS * (5 - 1) + REVEAL_DURATION_MS;
/** Win celebration tile bounce stagger. */
export const WIN_STAGGER_MS = 90;
/** Affirmation card appears after win/loss. */
export const AFFIRMATION_DELAY_MS = 1100;

/** Hub card stagger. */
export const HUB_STAGGER_S = 0.08;

export { easing, timings };
